<script setup lang="ts">
import { RouterLink } from 'vue-router';
import { Button } from '@/components/ui/button';
import { useHead } from '@unhead/vue';
import ScrollReveal from '@/components/ui/animation/ScrollReveal.vue';
import {
  APP_CTA_TO,
  TELEGRAM_CTA_LABEL,
  TELEGRAM_BOT_URL,
} from '@app/presentation/components/site/constants';
import PaperLetter from '@app/presentation/components/paper/PaperLetter.vue';
import SeoCrumbs from '@app/presentation/components/seo/SeoCrumbs.vue';
import SeoFaqItem from '@app/presentation/components/seo/SeoFaqItem.vue';
import SeoFinalCta from '@app/presentation/components/seo/SeoFinalCta.vue';

// Distinct facet from the home page: home owns the `таро онлайн бесплатно`
// head (readings hub); this page owns the `гадание онлайн / погадать сейчас`
// instant-tool facet. Self-canonical + cross-linked, never canonical→home.
const TITLE = 'Гадание таро онлайн — погадать бесплатно сейчас | Таро Мяу';
const DESCRIPTION =
  'Погадать на таро онлайн бесплатно и без регистрации — задай вопрос, и Таро Кот разложит карты именно под твою ситуацию. Бережно, честно, решение за тобой.';
const URL = 'https://tarotmeow.ru/gadanie-onlayn';

const CTA_LABEL = 'Разложить карты бесплатно';

const steps = [
  {
    n: '1',
    title: 'Задай свой вопрос',
    text: 'Спроси о том, что не спится спросить вслух — про отношения, работу или выбор. Своими словами, без специального словаря.',
  },
  {
    n: '2',
    title: 'Карты лягут под тебя',
    text: 'Не случайная картинка на всех, а живой расклад именно под твою ситуацию и твой вопрос.',
  },
  {
    n: '3',
    title: 'Получи тёплый разбор',
    text: 'Таро Кот объяснит расклад по-человечески — без эзотерического тумана и без осуждения.',
  },
];

const valueProps = [
  {
    title: 'Твой вопрос, а не общий шаблон',
    text: 'Живая AI-интерпретация под твой вопрос и твои карты — а не один и тот же текст для всех, как у гадалок-автоматов.',
  },
  {
    title: 'Честно, без сладкой лжи',
    text: 'Карты — это зеркало, а не приговор. Если расклад невесёлый, скажем бережно, но честно. Решение всё равно остаётся за тобой.',
  },
  {
    title: 'Кот, который рядом в любой час',
    text: 'Спрашивай хоть в два часа ночи — Таро Кот не оценивает и никуда не уходит. А карта дня остаётся с тобой бесплатно каждый день.',
  },
];

const faq = [
  {
    question: 'Гадание на таро правда бесплатное?',
    answer:
      'Да. Карта дня — бесплатно каждый день, без лимита. И три полных расклада, чтобы разобрать серьёзную историю, — тоже бесплатно. Без скрытых звёздочек: если решишь остаться без лимитов, цена — это просто цена.',
  },
  {
    question: 'Чем это лучше обычных онлайн-гадалок?',
    answer:
      'Обычный виджет выдаёт случайную карту и один и тот же текст для всех. Таро Кот читает именно твой вопрос и то, как легли твои карты, — и говорит по-человечески, без эзотерического тумана.',
  },
  {
    question: 'А если карты скажут что-то плохое?',
    answer:
      'Мы не пугаем и не обнадёживаем зря. Если расклад сложный — скажем бережно и по-честному. Помни: таро помогает думать, а не предсказывает будущее. Решение всегда остаётся за тобой.',
  },
];

const related = [
  { to: '/rasklady', label: 'Все расклады Таро', note: '16 видов раскладов на любую ситуацию' },
  { to: '/rasklady/karta-dnya', label: 'Карта дня', note: 'Короткий совет на сегодня — бесплатно' },
  { to: '/karty/da-ili-net', label: 'Таро: да или нет', note: 'Быстрый ответ по значению карт' },
];

useHead({
  title: TITLE,
  meta: [
    { name: 'description', content: DESCRIPTION },
    {
      name: 'keywords',
      content:
        'гадание таро онлайн, гадание онлайн бесплатно, погадать на таро, гадание на картах таро, таро гадание онлайн',
    },
    { property: 'og:title', content: TITLE },
    { property: 'og:description', content: DESCRIPTION },
    { property: 'og:image', content: 'https://tarotmeow.ru/og-image.jpg' },
    { property: 'og:locale', content: 'ru_RU' },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: 'Таро Мяу' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: TITLE },
    { name: 'twitter:description', content: DESCRIPTION },
    { name: 'twitter:image', content: 'https://tarotmeow.ru/og-image.jpg' },
  ],
  link: [
    { rel: 'canonical', href: URL },
    { rel: 'alternate', hreflang: 'ru', href: URL },
    { rel: 'alternate', hreflang: 'x-default', href: URL },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Главная', item: 'https://tarotmeow.ru/' },
          { '@type': 'ListItem', position: 2, name: 'Гадание онлайн', item: URL },
        ],
      }),
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      }),
    },
  ],
});
</script>

<template>
  <div class="py-16 md:py-24">
    <div class="site-container">
      <SeoCrumbs :items="[{ label: 'Главная', to: '/' }, { label: 'Гадание онлайн' }]" />

      <!-- Hero -->
      <header class="mb-14 text-center md:mb-20">
        <div class="type-overline mb-3.5 text-primary-ink">Гадание онлайн</div>
        <h1 class="type-display mx-auto max-w-[900px]">Гадание на таро онлайн — бесплатно, с котом, который слушает</h1>
        <p class="type-body mx-auto mt-4 max-w-2xl text-muted-foreground md:text-[17px]">
          Задай вопрос, который не спится задать вслух. Таро Кот разложит карты именно под твою
          ситуацию — не один шаблон на всех, а живой расклад. Бережно, честно и без осуждения.
        </p>
        <div class="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button
            as-child
            size="lg"
          >
            <RouterLink :to="APP_CTA_TO">{{ CTA_LABEL }}</RouterLink>
          </Button>
          <Button
            as-child
            variant="outline"
            size="lg"
          >
            <a
              :href="TELEGRAM_BOT_URL"
              target="_blank"
              rel="noopener"
            >{{ TELEGRAM_CTA_LABEL }}</a>
          </Button>
        </div>
      </header>

      <!-- The article itself: one taped letter at a reading measure. -->
      <ScrollReveal class="mx-auto mb-16 max-w-[760px] md:mb-24">
        <PaperLetter class="md:px-12 md:pb-12 md:pt-14">
          <!-- Как это работает -->
          <section>
            <div class="type-overline mb-3 text-primary-ink">Как это работает</div>
            <h2 class="type-title">Три шага до ответа</h2>
            <div class="seo-lines mt-6">
              <div
                v-for="step in steps"
                :key="step.n"
                class="flex gap-4 py-5"
              >
                <span class="seo-disc type-app-heading h-10 w-10">
                  {{ step.n }}
                </span>
                <div>
                  <h3 class="type-subheading text-foreground">{{ step.title }}</h3>
                  <p class="letter-text type-body mt-1.5 text-muted-foreground">{{ step.text }}</p>
                </div>
              </div>
            </div>
          </section>

          <!-- Value props -->
          <section class="mt-10 md:mt-14">
            <div class="type-overline mb-3 text-primary-ink">Почему Таро Мяу</div>
            <h2 class="type-title">Не гадалка-автомат, а тёплый разговор</h2>
            <div class="seo-lines mt-6">
              <div
                v-for="prop in valueProps"
                :key="prop.title"
                class="py-5"
              >
                <h3 class="type-subheading text-foreground">{{ prop.title }}</h3>
                <p class="letter-text type-body mt-1.5 text-muted-foreground">{{ prop.text }}</p>
              </div>
            </div>
          </section>
        </PaperLetter>
      </ScrollReveal>

      <!-- FAQ -->
      <section class="mb-16 md:mb-24">
        <ScrollReveal class="mb-8 text-center md:mb-10">
          <div class="type-overline mb-3 text-primary-ink">Частые вопросы</div>
          <h2 class="type-title">Коротко о главном</h2>
        </ScrollReveal>
        <div class="mx-auto max-w-[820px] space-y-3.5">
          <ScrollReveal
            v-for="(item, i) in faq"
            :key="i"
            :delay="((i % 3) + 1) * 80"
          >
            <SeoFaqItem
              :question="item.question"
              :answer="item.answer"
            />
          </ScrollReveal>
        </div>
      </section>

      <!-- Related / internal links -->
      <section class="mb-16 md:mb-24">
        <ScrollReveal class="mb-8 text-center md:mb-10">
          <div class="type-overline mb-3 text-primary-ink">Куда дальше</div>
          <h2 class="type-title">Выбери свой расклад</h2>
        </ScrollReveal>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
          <ScrollReveal
            v-for="(link, i) in related"
            :key="link.to"
            :delay="(i + 1) * 80"
          >
            <RouterLink
              :to="link.to"
              class="ar-lift seo-press paper-sheet group flex h-full flex-col p-5 md:p-6"
            >
              <h3 class="type-subheading text-foreground transition-colors group-hover:text-primary-ink">{{ link.label }}</h3>
              <p class="type-caption mt-2">{{ link.note }}</p>
            </RouterLink>
          </ScrollReveal>
        </div>
      </section>

      <!-- Bottom CTA -->
      <ScrollReveal>
        <SeoFinalCta cat>
          <h2 class="type-title">Спроси карты прямо сейчас</h2>
          <p class="type-body mx-auto mt-3.5 max-w-xl text-muted-foreground md:text-[17px]">
            Гадание на таро онлайн — бесплатно и без регистрации. Задай вопрос, и Таро Кот разложит
            карты именно под твою историю.
          </p>
          <div class="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button
              as-child
              size="lg"
            >
              <RouterLink :to="APP_CTA_TO">{{ CTA_LABEL }}</RouterLink>
            </Button>
            <Button
              as-child
              variant="outline"
              size="lg"
            >
              <a
                :href="TELEGRAM_BOT_URL"
                target="_blank"
                rel="noopener"
              >{{ TELEGRAM_CTA_LABEL }}</a>
            </Button>
          </div>
        </SeoFinalCta>
      </ScrollReveal>
    </div>
  </div>
</template>

<style scoped src="../components/seo/seo-paper.css"></style>

<style scoped>
@media (hover: hover) {
  .ar-lift:hover {
    translate: 0 -2px;
  }
}
</style>
